// SettingsContext.tsx
import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { NewsArticle } from '../components/organisms/NewsSection';
// eslint-disable-next-line import/no-cycle
import { FaqArticle } from '../components/organisms/PopularArticles';
import { MenuCardItem } from '../components/organisms/MenuCardList';
import { FooterLink } from '../components/organisms/Footer';
import i18n from '../i18n';
import { Loader } from '../components/organisms/Loader';
/* eslint-disable import/no-cycle */
import { SectionData, Settings, TranslationData } from '../interfaces';
import { updateConfigVersion, GlobalConfig, log } from '../utils';

const BLOB_CONTAINER_NAME = process.env.REACT_APP_BLOB_CONTAINER_NAME || '';

export interface Configs {
  primaryBgColor: string;
  secondaryBgColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  primaryAccentColor: string;
  secondaryAccentColor: string;
  tertiaryAccentColor: string;
  gradientAccentColor: string;
  font: string;

  showHeader: boolean;
  mainHeaderBgColor: string;
  mainHeaderTextColor: string;
  headerlogoUrl: string; // Allow null for optional image
  showMainHeader: boolean;
  showSubHeader: boolean;
  showHeaderLogo: boolean;
  headerLogoSize?: { base: string; lg: string };

  showBanner: boolean;
  isBannerFullScreen: boolean;
  showSearchBar: boolean;
  searchBarTextColor: string;
  searchBarBgColor?: string;
  placeholderColor?: string;
  showOverlay: boolean;
  bannerImageUrl: string;
  bannerBgColor: string;
  bannerTextColor: string;
  bannerTextSize?: string;

  showNewsSection: boolean;
  paginationSelectedPageBgColor?: string;
  paginationSelectedPageTextColor?: string;
  categoriesHeadingTextColor?: string;
  newsHeadlineTextColor?: string;

  showMenuCards: boolean;
  showPopularArticles: boolean;
  showSectionGrid: boolean;

  showFooter: boolean;
  footerLogoUrl: string;
  footerBackgroundColor: string;
  footerTextColor: string;
  showFooterLogo: boolean;
  showFooterLinks: boolean;
  footerLogoSize?: { base: string; lg: string };
  openFooterLinkInNewTab: boolean;

  showContactUsButton: boolean;
  contactUsButtonBgColor: string;
  contactUsButtonTextColor: string;
  needHelpTextColor?: string;
  needHelptext?: string;

  userChatTextColor: string;
  helpshiftChatTextColor: string;
  userChatBgColor: string;
  helpshiftChatBgColor: string;

  showNotificationBanner: boolean;
  notificationBannerBgColor: string;
  notificationBannerTextColor: string;
  headerTextColors: string;
  ticketViewPageLogoUrl: string;
  myTicketPageLogoUrl: string;
  contactUsLogo: string;
  rocksImageUrl: string;

  tableHeadingTextColor: string;
  faqBodyCharLimit: number;
  showJaggedBorder: boolean;
  showRocks: boolean;

  gaMeasurementId: string; // New property for Google Analytics measurement ID
  enableGoogleAnalytics: boolean; // New property to enable/disable Google Analytics

  useOneTrust: boolean; // New property for OneTrust usage
  oneTrustScriptId: string; // New property for OneTrust script ID
  showOneTrustCookieButton: boolean; // New property for showing OneTrust button
  useCustomOneTrustButtonStyle: boolean; // New property for custom button style
  oneTrustGAConsentId: string; // New property for OneTrust GA consent group ID
  showLogs: boolean;

  contactUsDisclaimerToastTextColor?: string;
  contactUsDisclaimerToastBgColor?: string;
}

export interface Section {
  name: string;
  articles: string[];
}

export interface SectionIcons {
  id: string;
  name: string;
  property: 'icon' | 'svg';
  svg?: string;
  icon: string;
}

export interface Data {
  sectionIcons: SectionIcons[];
  gameTitle: string;
  platforms: string[]; // Array of string for platforms
  languages: string[]; // Array of string for languages
  popularArticles: FaqArticle[]; // Assume FaqArticle type exists elsewhere
  menuCards: MenuCardItem[]; // Assume MenuCard type exists elsewhere
  footerLinks: FooterLink[]; // Assume FooterLink type exists elsewhere
  footerNote: string;
  mainHeading: string;
  sections_data: SectionData[];
  notifications: Notification[];
  oneTrustCookieButtonText?: string; // New property for OneTrust button text
  contactUsDisclaimerToastText?: {
    activeIssue: string;
    noActiveIssue: string;
  };
}

export interface Notification {
  id: string;
  message: string;
  startTime: string; // ISO format
  endTime: string; // ISO format
}

export interface FAQ {
  section_id: string;
  'published?': boolean;
  publish_id: string;
  platform_types: string[];
  slug: string;
  issue_tags?: string[];
  app_id: string;
  updated_at?: number;
  id: string;
  last_updated_by?: string;
  created_at?: number;
  translations: Record<string, any>;
  section_translations: Record<string, { title: string }>;
  linked_faq_ids?: string[];
}

export interface FormFieldOption {
  question?: string[]; // IDs of dependent questions
  showIf?: string[]; // For dependent dropdowns
}

export interface FormFieldOptions {
  [optionLabel: string]: FormFieldOption;
}
export interface FormField {
  id: string;
  question: string;
  description?: string;
  placeholder?: string;
  required: boolean;
  type:
    | 'singleline'
    | 'multiline'
    | 'email'
    | 'tel'
    | 'date'
    | 'password'
    | 'checkbox'
    | 'dropdown'
    | 'file'
    | 'radio'
    | 'number'
    | 'url'
    | 'heading'
    | 'information';
  dependent?: boolean;
  linked_cif_key?: string;
  options?: FormFieldOptions;
  linked_cif_options?: { label: string; option_id: string }[];
  isUserFirstMessage?: boolean;
}

export interface CifFeilds {
  key: string;
  type:
    | 'email'
    | 'tel'
    | 'date'
    | 'password'
    | 'checkbox'
    | 'dropdown'
    | 'file'
    | 'radio'
    | 'number'
    | 'url'
    | 'heading'
    | 'singleline'
    | 'multiline';
  label: string;
  id: string;
  is_editable: boolean;
  value?: string;
  metadata_type?: string;
  metadata_key?: string;
  data?: {
    placeholder?: string;
    options?: { label: string; option_id: string }[];
  };
}

export interface CifData {
  data: CifFeilds[];
  is_agent: boolean;
}

interface SettingsContextType {
  settings: Settings | null;
  setSettings: React.Dispatch<React.SetStateAction<Settings | null>>;
  saveSettings: (
    newSettings: Settings,
    translationData: TranslationData,
    contactUsData: FormField[],
    newNewsArticles: NewsArticle[],
  ) => Promise<void>;

  saveNewsArticlesOnly: (newNewsArticles: NewsArticle[]) => Promise<void>;
  faqs: FAQ[] | null;
  appId: string;
  language: string;
  setLanguage: (newLanguage: string) => void;
  supportedLanguages: { label: string; value: string }[];
  setFaqs: React.Dispatch<React.SetStateAction<FAQ[] | null>>;
  sectionsData: SectionData[] | null;
  setSectionsData: React.Dispatch<React.SetStateAction<SectionData[] | null>>;
  contactUsFormData: FormField[] | null;
  setContactUsFormData: React.Dispatch<React.SetStateAction<FormField[] | null>>;
  cifsData: CifData;
  adminApiKey: string | null;
  setAdminApiKey: React.Dispatch<React.SetStateAction<string | null>>;
  popularArticlesData: FaqArticle[] | null;
  dummyMyTicketsData: any;
  globalConfigData: GlobalConfig | null;
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  adminPanelStructData: any;
  hamburgerMenuLinks: any;
  newsArticles: NewsArticle[] | null;
}

// Create a context for the settings
export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Create a custom hook to access settings
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// function getSectionWiseFAQs(data: FAQ[], language: string, appId: string): SectionData[] {
//   const sectionsData: Record<string, SectionData> = {};
//   data.forEach(faq => {
//     if (faq.app_id !== appId) return;

//     const sectionTranslation = faq.section_translations?.[language] || faq.section_translations?.en;
//     const faqTranslation = faq.translations?.[language] || faq.translations?.en;

//     if (!sectionTranslation || !faqTranslation) return;

//     const sectionId = faq.section_id;

//     if (!sectionsData[sectionId]) {
//       sectionsData[sectionId] = {
//         section_id: sectionId,
//         section_name: sectionTranslation.title,
//         faq_data: [],
//       };
//     }

//     sectionsData[sectionId].faq_data.push({
//       publish_id: faq.publish_id,
//       faq_title: faqTranslation.title,
//       faq_body: faqTranslation.body,
//     });
//   });

//   return Object.values(sectionsData);
// }

function getSectionWiseFAQs(data: FAQ[], language: string, appId: string): SectionData[] {
  const sectionsData: Record<string, SectionData> = {};
  data.forEach(faq => {
    if (faq.app_id !== appId) return;

    const sectionTranslation = faq.section_translations?.[language] || faq.section_translations?.en;
    const faqTranslation = faq.translations?.[language] || faq.translations?.en;

    if (!sectionTranslation || !faqTranslation) return;

    const sectionId = faq.section_id;

    if (!sectionsData[sectionId]) {
      sectionsData[sectionId] = {
        section_id: sectionId,
        section_name: sectionTranslation.title,
        faq_data: [],
      };
    }

    sectionsData[sectionId].faq_data.push({
      publish_id: faq.publish_id,
      id: faq.id,
      category: sectionTranslation.title,
      faq_title: faqTranslation.title,
      faq_body: faqTranslation.body,
      faq_updated_at: faqTranslation.content_updated_at,
    });
  });

  return Object.values(sectionsData);
}
export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [language, setLanguage] = useState<string>(i18n.language?.split('-')[0] || 'en');
  const [supportedLanguages, setSupportedLanguages] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [faqs, setFaqs] = useState<FAQ[] | null>(null);
  const [sectionsData, setSectionsData] = useState<SectionData[] | null>(null);
  const [contactUsFormData, setContactUsFormData] = useState<FormField[] | null>(null);
  const [cifsData, setCifsData] = useState<CifData>({
    data: [],
    is_agent: false,
  });
  const [popularArticlesData, setPopularArticles] = useState<FaqArticle[] | null>(null);
  const [dummyMyTicketsData, setDummyMyTicketsData] = useState<FaqArticle[] | null>(null);
  const [adminPanelStructData, setAdminPanelStructData] = useState<any>(null);
  const [hamburgerMenuLinks, setHamburgerMenuLinks] = useState<
    { [key: string]: string }[] | undefined
  >(undefined);
  const [newsArticles, setNewsArticles] = useState<NewsArticle[] | null>(null);

  const [appId] = useState(process.env.REACT_APP_APP_ID || '');
  const [adminApiKey, setAdminApiKey] = useState<string | null>(null);

  const [globalConfigData, setGlobalConfigData] = useState<GlobalConfig | null>(null);

  const [unreadCount, setUnreadCount] = useState(0);

  // Function to save settings to the server
  const saveSettingsToServer = async (
    newSettings: Settings,
    translationData: TranslationData,
    contactUsData: FormField[],
    newNewsArticles: NewsArticle[],
  ) => {
    try {
      const data = JSON.stringify([
        {
          name: 'config.json',
          type: 'json',
          path: BLOB_CONTAINER_NAME,
          data: JSON.stringify(newSettings, null, 2),
        },
        {
          name: 'en.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}/translations`,
          data: JSON.stringify(translationData, null, 2),
        },
        {
          name: 'contact-us-formdata.json',
          type: 'json',
          path: BLOB_CONTAINER_NAME,
          data: JSON.stringify(contactUsData, null, 2),
        },
        {
          name: 'newsArticles.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}`,
          data: JSON.stringify(newNewsArticles, null, 2),
        },
        {
          name: 'globalConfig.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}`,
          data: JSON.stringify(updateConfigVersion(globalConfigData ?? undefined)),
        },
      ]);
      const configData = {
        method: 'post',
        maxBodyLength: Infinity,
        // url: 'https://catalysthc.ms.helpshift.live/api/fe-config',
        url: 'https://demo-hc-api.ms.helpshift.live/api/fe-config',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${adminApiKey ?? ''}`,
        },
        data,
      };

      const configResponse = await axios.request(configData);
      // eslint-disable-next-line
      // console.log(JSON.stringify(configResponse.data));
      log(settings?.configs.showLogs, JSON.stringify(configResponse.data));
      await Promise.all(
        supportedLanguages.map(async lang => {
          const translationUrl = `https://demo-hc-api.ms.helpshift.live/api/lang-translation?lang=${lang.value}&path=${BLOB_CONTAINER_NAME}/translations`;
          const translationConfig = {
            method: 'post',
            maxBodyLength: Infinity,
            url: translationUrl,
            headers: {
              Authorization: `Basic ${adminApiKey ?? ''}`,
            },
            data: translationData,
          };

          try {
            const translationResponse = await axios.request(translationConfig);
            // eslint-disable-next-line
            // console.log(
            //   `Translation API Response for ${lang.label}:`,
            //   JSON.stringify(translationResponse.data),
            // );
            log(
              settings?.configs.showLogs,
              `Translation API Response for ${lang.label}:`,
              JSON.stringify(translationResponse.data),
            );
          } catch (translationError) {
            // eslint-disable-next-line
            console.error(`Error in Translation API call for ${lang.label}:`, translationError);
            throw translationError;
          }
        }),
      );
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error saving settings:', error);
      throw error;
    }
  };

  // ─── News-only save ─────────────────────────────────────────────────────────
  // Touches ONLY newsArticles.json. Does NOT bump globalConfig version.
  // Throws on failure — callers must await and guard their local state updates.
  const saveNewsArticlesOnlyToServer = async (newNewsArticles: NewsArticle[]) => {
    try {
      const payload = JSON.stringify([
        {
          name: 'newsArticles.json',
          type: 'json',
          path: BLOB_CONTAINER_NAME,
          data: JSON.stringify(newNewsArticles, null, 2),
        },
        {
          name: 'globalConfig.json',
          type: 'json',
          path: `${BLOB_CONTAINER_NAME}`,
          data: JSON.stringify(updateConfigVersion(globalConfigData ?? undefined)),
        },
      ]);

      const response = await axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        // url: 'https://catalysthc.ms.helpshift.live/api/fe-config',
        url: 'https://demo-hc-api.ms.helpshift.live/api/fe-config',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${adminApiKey ?? ''}`,
        },
        data: payload,
      });

      log(settings?.configs.showLogs, 'newsArticles.json saved:', JSON.stringify(response.data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving newsArticles:', error);
      throw error;
    }
  };
  // New function to handle saving settings
  const saveSettings = async (
    newSettings: Settings,
    translationData: TranslationData,
    contactUsData: FormField[],
    newNewsArticles: NewsArticle[],
  ) => {
    await saveSettingsToServer(newSettings, translationData, contactUsData, newNewsArticles);
  };

  const saveNewsArticlesOnly = async (newNewsArticles: NewsArticle[]) => {
    await saveNewsArticlesOnlyToServer(newNewsArticles);
  };

  // Use useMemo to memoize the value prop
  const value = useMemo(
    () => ({
      settings,
      setSettings,
      saveSettings,
      saveNewsArticlesOnly,
      faqs,
      appId,
      language,
      setLanguage,
      supportedLanguages,
      setFaqs,
      sectionsData,
      setSectionsData,
      contactUsFormData,
      setContactUsFormData,
      cifsData,
      adminApiKey,
      setAdminApiKey,
      popularArticlesData,
      dummyMyTicketsData,
      globalConfigData,
      unreadCount,
      setUnreadCount,
      adminPanelStructData,
      hamburgerMenuLinks,
      newsArticles,
    }),
    [
      settings,
      setSettings,
      faqs,
      appId,
      language,
      sectionsData,
      contactUsFormData,
      supportedLanguages,
      cifsData,
      adminApiKey,
      setAdminApiKey,
      popularArticlesData,
      dummyMyTicketsData,
      globalConfigData,
      unreadCount,
      setUnreadCount,
      adminPanelStructData,
      hamburgerMenuLinks,
      newsArticles,
    ],
  );

  useEffect(() => {
    // Simulate API call to fetch settings
    const fetchSettings = async () => {
      try {
        const cdnurl = process.env.REACT_APP_CDN_URL;
        const globalConfigRes = await fetch(`${cdnurl}/globalConfig.json`);
        const globalConfig = await globalConfigRes.json(); // Parse response as JSON
        setGlobalConfigData(globalConfig);

        // Now fetch all required data using the URLs from globalConfig
        const [
          settingsRes,
          faqsRes,
          contactUsFormRes,
          supportedLanguagesRes,
          cifsDataRes,
          popularArticles,
          dummyDataForTickets,
          adminPanelStructure,
          hamburgerMenuNavLinks,
          newsArticlesRes,
        ] = await Promise.all([
          fetch(globalConfig.config),
          fetch(globalConfig.faqs),
          fetch(globalConfig.contactUs),
          fetch(globalConfig.supportedLanguages),
          fetch(globalConfig.cifs),
          fetch(globalConfig.popularArticles),
          fetch(globalConfig.dummyDataForTickets),
          fetch(globalConfig.adminPanelStructure),
          fetch(globalConfig.hamburgerMenuLinks),
          fetch(globalConfig.newsArticles),
        ]);
        // Check if both responses are successful
        if (!settingsRes.ok || !faqsRes.ok || !newsArticlesRes.ok) {
          throw new Error('One or more network responses were not ok');
        }

        // Parse both JSON responses concurrently
        const [
          data,
          faqArticles,
          contactUsFormFields,
          supportedLanguagesData,
          cifs,
          popularArticlesAnalytics,
          dummyTicketData,
          adminPanelStructureData,
          hamburgerMenuLinksData,
          newsArticlesData,
        ] = await Promise.all([
          settingsRes.json(),
          faqsRes.json(),
          contactUsFormRes.json(),
          supportedLanguagesRes.json(),
          cifsDataRes.json(),
          popularArticles.json(),
          dummyDataForTickets.json(),
          adminPanelStructure.json(),
          hamburgerMenuNavLinks.json(),
          newsArticlesRes.json(),
        ]);

        const supportedLanguagesArray = Object.entries(supportedLanguagesData).map(
          ([val, label]) => ({
            label: label as string,
            value: val as string,
          }),
        );

        setSupportedLanguages(supportedLanguagesArray);

        // Process FAQs data
        const sections = getSectionWiseFAQs(faqArticles, language, appId);

        // set the sections data in context
        setSectionsData(sections);

        // set the fetched faqs
        setFaqs(faqArticles);

        setContactUsFormData(contactUsFormFields);
        setCifsData(cifs);

        setPopularArticles(popularArticlesAnalytics);
        setDummyMyTicketsData(dummyTicketData);
        setAdminPanelStructData(adminPanelStructureData);
        setHamburgerMenuLinks(hamburgerMenuLinksData);
        setNewsArticles(newsArticlesData);

        // Set the fetched settings
        setSettings(data);
      } catch (error) {
        // eslint-disable-next-line
        console.error('Failed to fetch settings', error);
      }
    };
    fetchSettings();
  }, [language]); // rerender everytime language changes

  // Render only if settings is not null
  if (!settings) {
    return <Loader />; // Optionally show a loading message
  }
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
